import Phaser from "phaser";
import { ZoneId, DEFAULT_ZONE, isValidUsername, USERNAME_RULE_MSG } from "@ordinarydeveloper/shared";
import { API_URL } from "../serverUrl";

interface AuthUser {
  nickname: string;
  job: string | null;
  hasCharacter: boolean;
  currentZone: ZoneId;
}

export class AuthScene extends Phaser.Scene {
  private overlay!: HTMLDivElement;

  constructor() {
    super({ key: "AuthScene" });
  }

  async create(data: { notice?: string } = {}) {
    this.buildOverlay(data.notice);
    // 중복 접속으로 밀려나서 온 경우엔 세션 자동 복원을 하지 않는다 — 자동
    // 재접속하면 두 기기가 서로 밀어내는 무한 루프가 된다. (밀려난 쪽 세션은
    // 서버에서 이미 무효화됐지만, 같은 브라우저 두 탭처럼 세션을 공유하는
    // 경우가 있어 명시적으로 막는다.)
    if (!data.notice) await this.checkSession();
  }

  // ─── Session restore ──────────────────────────────────────────────────────

  private async checkSession() {
    try {
      const res = await fetch(`${API_URL}/auth/me`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json() as { user: AuthUser; joinToken: string };
        this.route(data.user, data.joinToken);
      }
    } catch {
      // no valid session — show the form as-is
    }
  }

  // ─── Routing ─────────────────────────────────────────────────────────────

  private route(user: AuthUser, joinToken: string) {
    this.removeOverlay();
    if (user.hasCharacter) {
      this.scene.start("GameScene", {
        nickname: user.nickname, job: user.job, joinToken,
        zone: user.currentZone ?? DEFAULT_ZONE,
      });
    } else {
      this.scene.start("CharacterScene");
    }
  }

  private removeOverlay() {
    if (this.overlay?.parentNode) this.overlay.parentNode.removeChild(this.overlay);
  }

  // ─── Overlay ─────────────────────────────────────────────────────────────

  private buildOverlay(notice?: string) {
    this.overlay = document.createElement("div");
    this.overlay.style.cssText = [
      "position:fixed;inset:0;display:flex;align-items:center;justify-content:center",
      "background:rgba(10,10,30,0.97);z-index:200;font-family:monospace",
    ].join(";");

    const card = document.createElement("div");
    card.style.cssText = [
      "width:360px;background:#1e1e3a;border-radius:12px;padding:32px 28px",
      "border:1px solid rgba(255,255,255,0.1);box-shadow:0 8px 32px rgba(0,0,0,0.6)",
    ].join(";");

    const title = document.createElement("h1");
    title.textContent = "OrdinaryDeveloper";
    title.style.cssText = [
      "color:#fff;font-size:18px;margin:0 0 24px;text-align:center",
      "letter-spacing:1px;font-weight:700",
    ].join(";");

    // Tabs
    const tabs = document.createElement("div");
    tabs.style.cssText = [
      "display:flex;margin-bottom:20px;border-radius:8px;overflow:hidden",
      "border:1px solid rgba(255,255,255,0.15)",
    ].join(";");

    const loginTab    = this.makeTab("로그인");
    const registerTab = this.makeTab("회원가입");
    tabs.append(loginTab, registerTab);

    // 다른 기기 로그인으로 밀려난 경우 등의 안내 배너
    const noticeBox = document.createElement("div");
    noticeBox.style.cssText = [
      "background:rgba(230,126,34,0.15);border:1px solid rgba(230,126,34,0.6)",
      "color:#f0b27a;font-size:12px;padding:10px 12px;border-radius:8px",
      "margin-bottom:16px;text-align:center;line-height:1.5",
      notice ? "" : "display:none",
    ].join(";");
    noticeBox.textContent = notice ?? "";

    const usernameInput = this.makeInput("text",     "아이디 (영문 소문자/숫자 4~20자)");
    const passwordInput = this.makeInput("password", "비밀번호 (6자 이상)");

    // 아이디 형식 인라인 에러 (입력창 바로 아래)
    const usernameError = document.createElement("div");
    usernameError.style.cssText = "color:#ff6b6b;font-size:11px;margin:-8px 0 12px 2px;display:none";
    usernameError.textContent = USERNAME_RULE_MSG;

    const submitBtn = document.createElement("button");
    submitBtn.textContent = "로그인";
    submitBtn.style.cssText = [
      "width:100%;padding:12px;background:#3d8b4e;color:#fff;border:none",
      "border-radius:8px;font-size:15px;cursor:pointer;margin-top:4px",
      "font-family:monospace;font-weight:600;transition:opacity 0.15s",
    ].join(";");
    submitBtn.onmouseenter = () => { submitBtn.style.opacity = "0.85"; };
    submitBtn.onmouseleave = () => { submitBtn.style.opacity = "1"; };

    const errorMsg = document.createElement("div");
    errorMsg.style.cssText = [
      "color:#ff6b6b;font-size:13px;margin-top:12px;min-height:18px;text-align:center",
    ].join(";");

    let isLogin = true;

    const switchTab = (toLogin: boolean) => {
      isLogin = toLogin;
      loginTab.style.cssText    = this.tabStyle(toLogin);
      registerTab.style.cssText = this.tabStyle(!toLogin);
      submitBtn.textContent     = toLogin ? "로그인" : "회원가입";
      errorMsg.textContent      = "";
      usernameError.style.display = "none";
    };

    loginTab.addEventListener("click",    () => switchTab(true));
    registerTab.addEventListener("click", () => switchTab(false));

    loginTab.style.cssText    = this.tabStyle(true);
    registerTab.style.cssText = this.tabStyle(false);

    const val = (wrap: HTMLDivElement) =>
      (wrap.querySelector("input") as HTMLInputElement).value;

    const handleSubmit = async () => {
      const username = val(usernameInput).trim();
      const password = val(passwordInput);

      if (!username || !password) {
        errorMsg.textContent = "아이디와 비밀번호를 입력해주세요";
        return;
      }
      // 회원가입은 아이디 형식을 제출 전에 인라인으로 검증한다
      if (!isLogin && !isValidUsername(username)) {
        usernameError.style.display = "block";
        return;
      }
      usernameError.style.display = "none";

      submitBtn.disabled    = true;
      submitBtn.textContent = "처리 중...";
      errorMsg.textContent  = "";

      try {
        const endpoint = isLogin ? "/auth/login" : "/auth/register";
        const res  = await fetch(`${API_URL}${endpoint}`, {
          method:      "POST",
          headers:     { "Content-Type": "application/json" },
          credentials: "include",
          body:        JSON.stringify({ username, password }),
        });
        const json = await res.json() as {
          error?: string;
          user?: AuthUser;
          joinToken?: string;
        };

        if (!res.ok) {
          errorMsg.textContent = json.error ?? "오류가 발생했습니다";
          return;
        }

        this.route(json.user!, json.joinToken!);
      } catch {
        errorMsg.textContent = "서버에 연결할 수 없습니다";
      } finally {
        submitBtn.disabled    = false;
        submitBtn.textContent = isLogin ? "로그인" : "회원가입";
      }
    };

    submitBtn.addEventListener("click", () => { void handleSubmit(); });

    // 입력을 다시 시작하면 인라인 에러를 지운다
    (usernameInput.querySelector("input") as HTMLInputElement).addEventListener("input", () => {
      usernameError.style.display = "none";
    });

    [usernameInput, passwordInput].forEach((wrap) => {
      (wrap.querySelector("input") as HTMLInputElement).addEventListener("keydown", (e) => {
        // 한글 IME 조합 확정 Enter(keyCode 229)로 중복 제출되는 것 방지
        if (e.isComposing || e.keyCode === 229) { e.stopPropagation(); return; }
        if (e.key === "Enter") void handleSubmit();
        e.stopPropagation();
      });
    });

    card.append(title, tabs, noticeBox, usernameInput, usernameError, passwordInput, submitBtn, errorMsg);
    this.overlay.appendChild(card);
    document.body.appendChild(this.overlay);
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  private tabStyle(active: boolean): string {
    return [
      "flex:1;padding:10px;border:none;cursor:pointer",
      "font-size:14px;font-family:monospace;transition:background 0.15s",
      `background:${active ? "#3d8b4e" : "transparent"}`,
      `color:${active ? "#fff" : "rgba(255,255,255,0.45)"}`,
    ].join(";");
  }

  private makeInput(type: string, placeholder: string): HTMLDivElement {
    const wrap  = document.createElement("div");
    wrap.style.cssText = "margin-bottom:12px";

    const input = document.createElement("input");
    input.type        = type;
    input.placeholder = placeholder;
    input.style.cssText = [
      "width:100%;box-sizing:border-box;padding:11px 14px",
      "background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15)",
      "color:#fff;font-size:14px;border-radius:8px;outline:none;font-family:monospace",
      "transition:border-color 0.15s",
    ].join(";");
    input.addEventListener("focus", () => { input.style.borderColor = "#3d8b4e"; });
    input.addEventListener("blur",  () => { input.style.borderColor = "rgba(255,255,255,0.15)"; });

    wrap.appendChild(input);
    return wrap;
  }

  private makeTab(label: string): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.textContent = label;
    return btn;
  }
}
